/**
 * @file BackupRunsList.tsx
 * @description История запусков бэкапа (phase08.9; phase19.26): карточки
 * строк на всех ширинах — у запуска мало полей, таблица не нужна.
 *
 * Этап — по-русски (`backupStageLabel`: «База данных», «Проверка
 * восстановления», «Медиа товаров»…). RUNNING старше суток помечается
 * «зависший»: скрипт не прислал итог, ждать бессмысленно. Строка `summary` —
 * «Итог запуска» целиком (шаг 7.1, Р5): полужирная (600) на подложке, чтобы
 * отличаться от этапов.
 */

import { AlertCircle, CheckCircle2, Loader2, TriangleAlert } from "lucide-react";

import { Badge } from "../../ui/badge";
import { BACKUP_RUN_BADGE } from "../status-meta";
import { EditorialCard } from "../../ui/EditorialCard";
import { cn } from "@/lib/utils";
import {
  RUN_STATUS_LABEL,
  TRIGGER_LABEL,
  backupStageLabel,
  formatBytes,
  formatDuration,
  isRunStuck,
  isRunSummary,
  type BackupRunDto,
} from "../../../lib/api-backups";

interface Props {
  runs: BackupRunDto[];
  runsLast30Days: number;
}

export function BackupRunsList({ runs, runsLast30Days }: Props) {
  return (
    <EditorialCard>
      <EditorialCard.Header
        title="История запусков"
        subtitle={`Последние ${runs.length} · за 30 дней всего ${runsLast30Days}`}
      />
      <EditorialCard.Body className="p-0">
        {runs.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-on-surface-variant">
            Запусков ещё не было. Запустите вручную или дождитесь расписания.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-on-surface/8">
            {runs.map((run) => (
              <RunRow key={run.id} run={run} />
            ))}
          </ul>
        )}
      </EditorialCard.Body>
    </EditorialCard>
  );
}

function RunRow({ run }: { run: BackupRunDto }) {
  const stuck = isRunStuck(run);
  const summary = isRunSummary(run);
  const details = [
    new Date(run.startedAt).toLocaleString("ru-RU"),
    run.durationMs ? formatDuration(run.durationMs) : null,
    run.bytesCopied ? formatBytes(run.bytesCopied) : null,
    run.objectsCount !== null ? `${run.objectsCount} объектов` : null,
  ].filter(Boolean);

  return (
    <li className={cn("flex items-start gap-3 px-4 py-3", summary && "bg-surface-low")}>
      <span className="mt-0.5 shrink-0">
        {run.status === "SUCCESS" ? (
          <CheckCircle2 className="size-4 text-success" aria-hidden />
        ) : run.status === "FAILED" ? (
          <AlertCircle className="size-4 text-destructive" aria-hidden />
        ) : stuck ? (
          <TriangleAlert className="size-4 text-warning" aria-hidden />
        ) : (
          <Loader2 className="size-4 animate-spin text-on-surface-variant" aria-hidden />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("text-sm text-on-surface", summary ? "font-semibold" : "font-medium")}>
            {backupStageLabel(run.bucket)}
          </span>
          <Badge variant={BACKUP_RUN_BADGE[run.status]}>{RUN_STATUS_LABEL[run.status]}</Badge>
          {stuck ? (
            <Badge variant="warning" title="Нет итога больше суток">
              зависший
            </Badge>
          ) : null}
          <Badge variant="outline">{TRIGGER_LABEL[run.triggeredBy] ?? run.triggeredBy}</Badge>
        </div>
        <div className="mt-1 text-xs text-on-surface-variant tabular-nums">
          {details.join(" · ")}
        </div>
        {run.errorMessage ? (
          <p className="mt-1 break-words text-xs text-destructive">{run.errorMessage}</p>
        ) : null}
      </div>
    </li>
  );
}
